import { PaymentModal } from '../../components/common/PaymentModal';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { ORDER_STATUSES } from '../../utils/statuses';
import { fmt, fmtDt, money, errMsg } from '../../utils/helpers';
import { Badge, Spinner, Tabs, ProgressBar, Select, Modal, ConfirmModal, Field } from '../../components/common/UI';
import { CommentsPanel, FilesPanel, ActivityLog } from '../../components/common/Panels';
import { OrderItemsPanel, ShipmentsPanel } from './OrderItemsPanel';
import { OrderComponentsPanel } from './OrderComponentsPanel';
import { ArrowLeft, Edit2, Save, X, Trash2, Plus, Truck } from 'lucide-react';
import toast from 'react-hot-toast';

function PaymentRow({ p, onDelete, canDelete }) {
  const fmtDate = (d) => d ? d.slice(0,10).split('-').reverse().join('.') : '';
  const fmtMoney = (v) => v ? (+v).toLocaleString('ru-RU') + ' ₽' : '—';
  return (
    <div className="p-3 bg-gray-800/60 border border-gray-800 rounded-lg group">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-0.5 flex-1">
          <div className="text-sm font-medium text-gray-200">{fmtMoney(p.amount)}</div>
          {(p.payer_name || p.receiver_name) && (
            <div className="text-xs text-gray-500">{p.payer_name || '—'} → {p.receiver_name || '—'}</div>
          )}
          {p.doc_number && (
            <div className="text-xs text-gray-500 mono">
              № {p.doc_number}{p.doc_date ? ' от ' + fmtDate(p.doc_date) : ''}
            </div>
          )}
          {p.payment_date && <div className="text-xs text-gray-500">Дата платежа: {fmtDate(p.payment_date)}</div>}
          {p.note && <div className="text-xs text-gray-600 italic">{p.note}</div>}
          <div className="text-xs text-gray-700">{p.by_name}</div>
        </div>
        {canDelete && (
          <button className="btn-ghost p-1 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            onClick={onDelete}><Trash2 size={14} /></button>
        )}
      </div>
    </div>
  );
}


const STATUS_OPTS = Object.entries(ORDER_STATUSES).map(([v, d]) => ({ value: v, label: d.label }));

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isProducer, isDealerAdmin, hasPerm } = useAuth();
  const canManageSettlementParties = (isProducer || isDealerAdmin) && hasPerm('orders', 'view_prices');
  const isNew = id === 'new';

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [tab, setTab] = useState('info');
  const [editing, setEditing] = useState(isNew);
  const [form, setForm] = useState({ title: '', description: '', dealer_id: '', payer_company_id: '', receiver_company_id: '', deadline: '' });
  const [saving, setSaving] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [progressModal, setProgressModal] = useState(false);
  const [newProgress, setNewProgress] = useState(0);
  const [paymentModal, setPaymentModal] = useState(false);
  const [dealerPct, setDealerPct] = useState(0);
  const [orderItems, setOrderItems] = useState([]);
  const [orderShipments, setOrderShipments] = useState([]);

  // Предупреждение при закрытии вкладки с несохранёнными данными
  useEffect(() => {
    const handler = (e) => {
      if (isNew || editing) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isNew, editing]);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const { data } = await api.get('/orders/' + id);
      setOrder(data);
      setForm({
        title: data.title || '', description: data.description || '',
        dealer_id: data.dealer_id || '', payer_company_id: data.payer_company_id || '',
        receiver_company_id: data.receiver_company_id || '',
        deadline: data.deadline ? data.deadline.slice(0, 10) : ''
      });
      setNewProgress(data.progress || 0);
      setNewStatus(data.status);
      // Загружаем позиции и отгрузки для расчёта
      try {
        const [{ data: itms }, { data: shps }] = await Promise.all([
          api.get('/orders/' + id + '/items'),
          api.get('/orders/' + id + '/shipments'),
        ]);
        setOrderItems(itms);
        setOrderShipments(shps);
      } catch { }
    } catch { navigate('/orders'); }
    finally { setLoading(false); }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!editing && !isNew) return;
    Promise.all([
      isProducer ? api.get('/admin/dealers') : Promise.resolve({ data: [] }),
      api.get('/admin/companies')
    ]).then(([d, c]) => {
      setDealers(d.data.map(x => ({ value: x.id, label: x.name })));
      setCompanies(c.data);
    });
  }, [editing, isNew, isProducer]);


  useEffect(() => {
    if (isNew || isProducer) return;
    const sync = async () => {
      try {
        const { data } = await api.get('/orders/' + id);
        setOrder(prev => {
          if (!prev) return data;
          if (prev.status === data.status) return prev;
          return { ...prev, ...data };
        });
        if (editing && data.status !== 'placed') setEditing(false);
      } catch {}
    };
    const timer = window.setInterval(sync, 15000);
    window.addEventListener('focus', sync);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', sync);
    };
  }, [id, isNew, isProducer, editing]);

  const handleOrderLocked = useCallback((freshOrder) => {
    if (freshOrder) setOrder(freshOrder);
    setEditing(false);
  }, []);

  useEffect(() => {
    if (!order?.payer_company_id) return;
    if (!isProducer) {
      setDealerPct(order?.dealer_pct || 0);
      return;
    }
    api.get('/admin/companies').then(({ data }) => {
      const co = data.find(c => c.id === order.payer_company_id);
      setDealerPct(co?.dealer_pct || 0);
    }).catch(() => {});
  }, [isProducer, order?.payer_company_id, order?.dealer_pct]);

  const targetDealerId = form.dealer_id || order?.dealer_id;
  const payerOpts = companies.filter(c => !targetDealerId || c.dealer_id === +targetDealerId).map(c => ({ value: c.id, label: c.name }));
  const receiverOpts = companies.filter(c => !c.dealer_id).map(c => ({ value: c.id, label: c.name }));
  const paymentPayerOpts = companies.filter(c => !targetDealerId || c.dealer_id === +targetDealerId).map(c => ({ value: c.id, label: c.name }));
  const paymentReceiverOpts = companies.filter(c => !c.dealer_id).map(c => ({ value: c.id, label: c.name }));

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post('/orders', form);
        toast.success('Заказ создан');
        navigate('/orders/' + data.id);
      } else {
        await api.patch('/orders/' + id + '/content', form);
        toast.success('Сохранено');
        setEditing(false);
        load();
      }
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const changeStatus = async () => {
    try {
      await api.patch('/orders/' + id + '/status', { status: newStatus, note: statusNote });
      toast.success('Статус изменён');
      setStatusModal(false);
      setStatusNote('');
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const changeProgress = async () => {
    try {
      await api.patch('/orders/' + id + '/progress', { progress: newProgress });
      toast.success('Прогресс обновлён');
      setProgressModal(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };


  const deleteOrder = async () => {
    try { await api.delete('/orders/' + id); toast.success('Заказ удалён'); navigate('/orders'); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const [deletePaymentConfirm, setDeletePaymentConfirm] = useState(null);

  const deletePayment = async (pid) => {
    try {
      await api.delete('/orders/' + id + '/payments/' + pid);
      toast.success('Платёж удалён');
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><Spinner size={32} /></div>;

  const orderContentUnlocked = isNew || order?.status === 'placed';
  const dealerCanEditWindow = !isNew && !isProducer
    ? (Date.now() - new Date(order?.created_at)) / 3600000 < 24
    : true;
  const dealerCanEditOrder = orderContentUnlocked && dealerCanEditWindow;

  const canEdit = isNew
    ? hasPerm('orders', 'create')
    : hasPerm('orders', 'edit_content') && orderContentUnlocked && (isProducer || dealerCanEditOrder);

  const st = order ? ORDER_STATUSES[order.status] : null;
  const showPrices = hasPerm('orders', 'view_prices');
  const showFullFinance = (isProducer || isDealerAdmin) && showPrices;
  const showPaymentDetails = showFullFinance;
  const totalPaid = showPaymentDetails ? (order?.payments?.reduce((s, p) => s + +p.amount, 0) || 0) : 0;

  // Расчёт отгруженного
  const shippedMap = {};
  orderShipments.forEach(s => s.items?.forEach(si => {
    shippedMap[si.order_item_id] = (shippedMap[si.order_item_id] || 0) + +si.quantity;
  }));
  const shippedRetail = orderItems.reduce((sum, item) => {
    const qty = shippedMap[item.id] || 0;
    return sum + qty * +item.unit_price;
  }, 0);
  const shippedDealer = orderItems.reduce((sum, item) => {
    const qty = shippedMap[item.id] || 0;
    const line = qty * +item.unit_price;
    return sum + (item.price_type === 'retail' ? line * (1 - (dealerPct || 0) / 100) : line);
  }, 0);
  const visibleOrderTotal = showFullFinance ? (order?.price_dealer || 0) : (order?.price_retail || 0);
  const totalOrderedQty = orderItems.reduce((s, i) => s + +i.quantity, 0);
  const totalShippedQty = orderItems.reduce((s, i) => s + (shippedMap[i.id] || 0), 0);
  const fullyShipped = orderItems.length > 0 && orderItems.every(i => (shippedMap[i.id] || 0) >= +i.quantity);
  const unreceivedShipments = orderShipments.filter(s => !s.received_at);
  const hasUnreceivedShipments = unreceivedShipments.length > 0;

  const visibleFiles = canManageSettlementParties ? (order?.files || []) : (order?.files || []).filter(f => f.file_type !== 'invoice');
  const hasInvoice = visibleFiles.some(f => f.file_type === 'invoice');
  const payStatus = !hasInvoice ? null
    : totalPaid <= 0 ? { label: 'Не оплачен', cls: 'bg-red-900/50 text-red-300 border-red-700' }
    : totalPaid >= (order?.price_dealer || 0) ? { label: 'Оплачен полностью', cls: 'bg-green-900/50 text-green-300 border-green-700' }
    : { label: 'Частично оплачен', cls: 'bg-yellow-900/50 text-yellow-300 border-yellow-700' };
  const TABS = [
    { id: 'info',      label: 'Информация' },
    { id: 'items',     label: 'Позиции' },
    { id: 'shipments', label: 'Отгрузки' },
    { id: 'comments',  label: 'Комментарии', count: order?.comments?.length },
    ...(isProducer ? [{ id: 'components', label: 'Комплектующие' }] : []),
    { id: 'files',     label: 'Файлы',       count: visibleFiles.length },
    ...(showPaymentDetails ? [{ id: 'payments', label: 'Оплата', count: order?.payments?.length }] : []),
    { id: 'history',   label: 'История' },
  ];

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <button className="btn-ghost p-2" onClick={() => navigate('/orders')}><ArrowLeft size={16} /></button>
          <div>
            {isNew ? <h1 className="text-lg font-bold text-gray-100">Новый заказ</h1> : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="mono text-blue-400 text-sm">{order?.order_number}</span>
                  {st && <Badge className={st.color}>{st.label}</Badge>}
                  {showPaymentDetails && visibleFiles.some(f => f.file_type === 'invoice') && (
                    <Badge className="bg-emerald-900/50 text-emerald-300 border-emerald-700">✓ Счёт выставлен</Badge>
                  )}
                  {showPaymentDetails && payStatus && <Badge className={payStatus.cls}>{payStatus.label}</Badge>}
                  {fullyShipped && (
                    <Badge className="bg-purple-900/50 text-purple-300 border-purple-700">✓ Отгружено полностью</Badge>
                  )}
                  {hasUnreceivedShipments && (
                    <Badge className="bg-amber-900/50 text-amber-300 border-amber-700">Отгружено, не получено{unreceivedShipments.length > 1 ? ` · ${unreceivedShipments.length}` : ''}</Badge>
                  )}
                </div>
                <h1 className="text-lg font-bold text-gray-100 mt-0.5">{order?.title}</h1>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && isProducer && hasPerm('orders', 'edit_status') && (
            <button className="btn-secondary" onClick={() => { setNewStatus(order.status); setStatusModal(true); }}>Статус</button>
          )}
          {!isNew && isProducer && hasPerm('orders', 'edit_progress') && (
            <button className="btn-secondary" onClick={() => { setNewProgress(order.progress); setProgressModal(true); }}>Прогресс</button>
          )}
          {!isNew && canEdit && !editing && (
            <button className="btn-secondary" onClick={() => setEditing(true)}><Edit2 size={14} /> Редактировать</button>
          )}
          {editing && !isNew && <button className="btn-ghost" onClick={() => setEditing(false)}><X size={14} /></button>}
          {editing && <button className="btn-primary" onClick={save} disabled={saving}>{saving ? <Spinner size={14} /> : <><Save size={14} /> Сохранить</>}</button>}
          {!isNew && hasPerm('orders', 'delete') && (
            <button className="btn-danger" onClick={() => setDeleteConfirm(true)}><Trash2 size={14} /></button>
          )}
        </div>
      </div>

      {!isNew && order && (
        <div className="card p-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-gray-500">Прогресс выполнения</span>
                <span className="mono text-sm text-gray-300">{order.progress}%</span>
              </div>
              <ProgressBar value={order.progress} />
            </div>
            {order.deadline && (
              <div className="text-xs text-right shrink-0">
                <div className="text-gray-400 font-medium">Дедлайн</div>
                <div className={new Date(order.deadline) < new Date() && order.status !== 'shipped' ? 'text-red-400' : 'text-gray-500'}>
                  {fmt(order.deadline)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isNew && (
        <div className="card p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Field label="Название заказа" required>
                <input className="input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Кратко опишите заказ" />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Описание / ТЗ">
                <textarea className="input resize-none" rows={5} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </Field>
            </div>
            {isProducer && (
              <Field label="Дилер" required>
                <Select value={form.dealer_id} onChange={v => setForm(f => ({ ...f, dealer_id: v }))} options={dealers} placeholder="Выберите дилера" />
              </Field>
            )}
            {canManageSettlementParties && (<>
              <Field label="Плательщик (дилер)">
                <Select value={form.payer_company_id} onChange={v => setForm(f => ({ ...f, payer_company_id: v }))} options={payerOpts} placeholder="Компания дилера" />
              </Field>
              <Field label="Получатель (моя компания)">
                <Select value={form.receiver_company_id} onChange={v => setForm(f => ({ ...f, receiver_company_id: v }))} options={receiverOpts} placeholder="Моя компания" />
              </Field>
            </>)}
            <Field label="Дедлайн">
              <input type="date" className="input" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} />
            </Field>
          </div>
          <div className="flex justify-end mt-6 gap-2">
            <button className="btn-secondary" onClick={() => navigate('/orders')}>Отмена</button>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? <Spinner size={14} /> : 'Создать заказ'}</button>
          </div>
        </div>
      )}

      {!isNew && (
        <div className="card overflow-hidden">
          {hasUnreceivedShipments && (
            <div className="mx-5 mt-5 flex items-start gap-3 rounded-xl border border-amber-800 bg-amber-950/40 px-4 py-3 text-sm text-amber-200">
              <Truck size={16} className="mt-0.5 shrink-0 text-amber-400" />
              <div>
                <div className="font-medium">Есть отгрузка без отметки о получении</div>
                <div className="text-xs text-amber-300/80 mt-1">
                  {unreceivedShipments.length === 1
                    ? 'Дилер должен подтвердить получение товара в разделе «Отгрузки».'
                    : `Дилер должен подтвердить получение по ${unreceivedShipments.length} отгрузкам в разделе «Отгрузки».`}
                </div>
              </div>
            </div>
          )}
          {!orderContentUnlocked && (
            <div className="mx-5 mt-5 flex items-start gap-3 rounded-xl border border-red-900 bg-red-950/35 px-4 py-3 text-sm text-red-200">
              <X size={16} className="mt-0.5 shrink-0 text-red-400" />
              <div>
                <div className="font-medium">Состав заказа и ТЗ заблокированы</div>
                <div className="text-xs text-red-300/80 mt-1">
                  После перевода заказа в статус «Изготовление» или дальше редактирование состава, комплектов, цен компонентов и ТЗ недоступно.
                </div>
              </div>
            </div>
          )}
          <Tabs tabs={TABS} active={tab} onChange={setTab} />
          <div className="p-5">
            {tab === 'info' && (
              <div className="space-y-6">
                {editing ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Field label="Название" required>
                        <input className="input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                      </Field>
                    </div>
                    <div className="md:col-span-2">
                      <Field label="Описание / ТЗ">
                        <textarea className="input resize-none" rows={5} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                      </Field>
                    </div>
                    {isProducer && (
                      <Field label="Дилер">
                        <Select value={form.dealer_id} onChange={v => setForm(f => ({ ...f, dealer_id: v }))} options={dealers} placeholder="Выберите дилера" />
                      </Field>
                    )}
                    {canManageSettlementParties && (<>
                      <Field label="Плательщик">
                        <Select value={form.payer_company_id} onChange={v => setForm(f => ({ ...f, payer_company_id: v }))} options={payerOpts} placeholder="Компания дилера" />
                      </Field>
                      <Field label="Получатель">
                        <Select value={form.receiver_company_id} onChange={v => setForm(f => ({ ...f, receiver_company_id: v }))} options={receiverOpts} placeholder="Моя компания" />
                      </Field>
                    </>)}
                    <Field label="Дедлайн">
                      <input type="date" className="input" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} />
                    </Field>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2 space-y-4">
                      <div>
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <div className="section-title">Описание / ТЗ</div>
                          {canEdit && (
                            <button className="btn-ghost h-8 px-3 text-xs" onClick={() => setEditing(true)}>
                              <Edit2 size={13} /> Изменить ТЗ
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-gray-300 whitespace-pre-wrap">{order?.description || <span className="text-gray-600">—</span>}</p>
                      </div>
                      <div>
                        <div className="section-title mb-3">История статусов</div>
                        {order?.statusLog?.map((s, i) => {
                          const stInfo = ORDER_STATUSES[s.status] || {};
                          return (
                            <div key={s.id} className="flex gap-3">
                              <div className="flex flex-col items-center">
                                <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                                {i < order.statusLog.length - 1 && <div className="w-px flex-1 bg-gray-800 my-1" />}
                              </div>
                              <div className="pb-3">
                                <Badge className={stInfo.color}>{stInfo.label}</Badge>
                                <div className="text-xs text-gray-500 mt-0.5">{fmtDt(s.changed_at)} · {s.by_name}</div>
                                {s.note && <div className="text-xs text-gray-400 italic mt-0.5">{s.note}</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="space-y-4">
                      {isProducer && <div><div className="section-title mb-1">Дилер</div><div className="text-sm text-gray-200">{order?.dealer_name}</div></div>}
                      {showPrices && showFullFinance && (
                        <div>
                          <div className="section-title mb-2">Финансы</div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-gray-500">РЦ итого:</span><span className="mono text-gray-300">{money(order?.price_retail)}</span></div>
                            {dealerPct > 0 && <div className="flex justify-between"><span className="text-gray-500">Дилерский %:</span><span className="text-gray-500">{dealerPct}%</span></div>}
                            <div className="flex justify-between border-t border-gray-800 pt-1"><span className="text-gray-500">К оплате:</span><span className="mono text-gray-200 font-medium">{money(visibleOrderTotal)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Оплачено:</span><span className={'mono ' + (totalPaid >= (visibleOrderTotal || 0) ? 'text-green-400' : 'text-yellow-400')}>{money(totalPaid)}</span></div>
                            {(visibleOrderTotal || 0) - totalPaid > 0 && (
                              <div className="flex justify-between font-medium">
                                <span className="text-gray-500">Не оплачено:</span>
                                <span className="mono text-red-400">{money((visibleOrderTotal || 0) - totalPaid)}</span>
                              </div>
                            )}

                            <div className="flex justify-between border-t border-gray-800 pt-1 mt-1"><span className="text-gray-500">Отгружено (РЦ):</span><span className="mono text-gray-300">{money(shippedRetail)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Отгружено (к оплате):</span><span className={'mono ' + (fullyShipped ? 'text-green-400' : 'text-gray-300')}>{money(showFullFinance ? shippedDealer : shippedRetail)}</span></div>
                            {fullyShipped && <div className="text-xs text-green-400 text-right">✓ Отгружено полностью</div>}
                            {!fullyShipped && orderItems.length > 0 && <div className="text-xs text-gray-600 text-right">Позиций: {totalShippedQty} / {totalOrderedQty}</div>}
                          </div>
                        </div>
                      )}
                      {showPrices && !showFullFinance && (
                        <div>
                          <div className="section-title mb-2">Стоимость</div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-gray-500">По позициям:</span><span className="mono text-gray-200 font-medium">{money(visibleOrderTotal)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Отгружено:</span><span className={'mono ' + (fullyShipped ? 'text-green-400' : 'text-gray-300')}>{money(shippedRetail)}</span></div>
                            {fullyShipped && <div className="text-xs text-green-400 text-right">✓ Отгружено полностью</div>}
                            {!fullyShipped && orderItems.length > 0 && <div className="text-xs text-gray-600 text-right">Позиций: {totalShippedQty} / {totalOrderedQty}</div>}
                          </div>
                        </div>
                      )}
                      {showFullFinance && <div><div className="section-title mb-1">Плательщик</div><div className="text-sm text-gray-300">{order?.payer_company_name || '—'}</div></div>}
                      {showFullFinance && <div><div className="section-title mb-1">Получатель</div><div className="text-sm text-gray-300">{order?.receiver_company_name || '—'}</div></div>}
                      {showPaymentDetails && visibleFiles.some(f => f.file_type === 'invoice') && (
                        <div>
                          <div className="section-title mb-1">Счета</div>
                          <div className="text-xs text-gray-400">
                            {visibleFiles.filter(f => f.file_type === 'invoice').map(f => f.original_name).join(', ')}
                          </div>
                        </div>
                      )}
                      <div><div className="section-title mb-1">Создан</div><div className="text-sm text-gray-400">{fmtDt(order?.created_at)}</div><div className="text-xs text-gray-600">{order?.created_by_name}</div></div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === 'items' && <OrderItemsPanel orderId={id} orderStatus={order?.status} canEditItems={hasPerm('orders', 'edit_content') && orderContentUnlocked && (isProducer || dealerCanEditOrder)} dealerPct={showFullFinance ? dealerPct : 0} showPrices={showPrices} onTotalsChange={() => {}} onRefresh={load} onOrderLocked={handleOrderLocked} />}
            {tab === 'shipments' && <ShipmentsPanel orderId={id} onRefresh={load} />}
            {tab === 'components' && isProducer && (
            <OrderComponentsPanel orderId={id} orderItems={order?.items || []} />
          )}
          {tab === 'comments' && <CommentsPanel entityType="order" entityId={id} comments={order?.comments} onRefresh={load} />}
            {tab === 'files' && <FilesPanel entityType="order" entityId={id} files={visibleFiles} onRefresh={load} />}

            {tab === 'payments' && showPaymentDetails && (
              <div className="space-y-4">
                {isProducer && (
                  <button className="btn-primary" onClick={() => setPaymentModal(true)}><Plus size={14} /> Добавить платёж</button>
                )}
                {order?.payments?.length === 0 && <p className="text-sm text-gray-600">Нет платежей</p>}
                {order?.payments?.map(p => (
                  <PaymentRow key={p.id} p={p}
                    canDelete={user?.role === 'superadmin'}
                    onDelete={() => setDeletePaymentConfirm(p)} />
                ))}
                {order?.payments?.length > 0 && (
                  <div className="border-t border-gray-800 pt-3 flex justify-between text-sm">
                    <span className="text-gray-500">Итого оплачено:</span>
                    <span className="mono font-medium text-gray-200">{money(totalPaid)}</span>
                  </div>
                )}
              </div>
            )}

            {tab === 'history' && <ActivityLog entries={order?.actLog} />}
          </div>
        </div>
      )}

      {statusModal && (
        <Modal title="Сменить статус" onClose={() => setStatusModal(false)} size="sm">
          <div className="space-y-4">
            <Field label="Новый статус"><Select value={newStatus} onChange={setNewStatus} options={STATUS_OPTS} /></Field>
            <Field label="Примечание"><input className="input" value={statusNote} onChange={e => setStatusNote(e.target.value)} /></Field>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setStatusModal(false)}>Отмена</button>
              <button className="btn-primary" onClick={changeStatus}>Применить</button>
            </div>
          </div>
        </Modal>
      )}

      {progressModal && (
        <Modal title="Обновить прогресс" onClose={() => setProgressModal(false)} size="sm">
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2"><span className="label">Прогресс</span><span className="mono text-blue-400 text-sm">{newProgress}%</span></div>
              <input type="range" min={0} max={100} step={5} value={newProgress} onChange={e => setNewProgress(+e.target.value)} className="w-full accent-blue-500" />
            </div>
            <ProgressBar value={newProgress} />
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setProgressModal(false)}>Отмена</button>
              <button className="btn-primary" onClick={changeProgress}>Сохранить</button>
            </div>
          </div>
        </Modal>
      )}

      {paymentModal && (
        <PaymentModal entityType="order" entityId={id} dealerId={order?.dealer_id}
          onClose={() => setPaymentModal(false)}
          onDone={load} />
      )}

      {deletePaymentConfirm && (
        <ConfirmModal message={'Удалить платёж на ' + money(deletePaymentConfirm.amount) + '?'}
          onConfirm={() => { deletePayment(deletePaymentConfirm.id); setDeletePaymentConfirm(null); }}
          onClose={() => setDeletePaymentConfirm(null)} />
      )}

      {deleteConfirm && (
        <ConfirmModal message={'Удалить заказ ' + order?.order_number + '? Это действие нельзя отменить.'}
          onConfirm={deleteOrder} onClose={() => setDeleteConfirm(false)} />
      )}
    </div>
  );
}
